// @figma file=pkCGby54zXzTsccXvHqzO6 node=3831:2727 name="Progress" set="shadcn-studio" verified=2026-03-21

import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Track variant config ─────────────────────────────────────────────────────

/**
 * Track (background rail) variants.
 * Size maps directly to Figma dimensions:
 *   sm → 6px · md → 8px (default) · lg → 12px
 */
export const progressTrackVariants = cva(
  'relative w-full overflow-hidden rounded-full bg-secondary',
  {
    variants: {
      size: {
        sm: 'h-1.5', // 6px  — Figma sizes=sm
        md: 'h-2', // 8px  — Figma sizes=md (default)
        lg: 'h-3', // 12px — Figma sizes=lg
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

// ─── Indicator variant config ─────────────────────────────────────────────────

/**
 * Indicator (fill bar) color variants.
 * Maps to semantic tokens — aligned with Figma colour dimensions.
 */
export const progressIndicatorVariants = cva('h-full w-full flex-1 transition-all', {
  variants: {
    variant: {
      default: 'bg-primary',
      success: 'bg-success',
      destructive: 'bg-destructive',
      info: 'bg-info',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
    VariantProps<typeof progressTrackVariants>,
    VariantProps<typeof progressIndicatorVariants> {
  /**
   * Current progress value in the range [0, max].
   * Pass `null` for indeterminate state (removes aria-valuenow).
   * @default 0
   */
  value?: number | null;
  /**
   * Maximum value. Must be a positive number.
   * @default 100
   */
  max?: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

const Progress = React.forwardRef<React.ElementRef<typeof ProgressPrimitive.Root>, ProgressProps>(
  ({ className, value = 0, max = 100, size, variant, ...props }, ref) => {
    // Clamp percentage to [0, 100]; null signals indeterminate.
    const percentage = value != null ? Math.min(100, Math.max(0, (value / max) * 100)) : null;

    return (
      <ProgressPrimitive.Root
        ref={ref}
        value={value ?? null}
        max={max}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value ?? undefined}
        className={cn(progressTrackVariants({ size }), className)}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={cn(progressIndicatorVariants({ variant }))}
          style={{ transform: `translateX(-${100 - (percentage ?? 0)}%)` }}
        />
      </ProgressPrimitive.Root>
    );
  },
);
Progress.displayName = 'Progress';

export { Progress };
